import { forwardRef } from 'react';
import type { PropsWithoutRef } from 'react';
import styled, { css } from 'styled-components';

import { defaultThemeProp } from '../../theme';
import useOS from '../../hooks/useOS';
import type { NoChildrenProp, RefElement, WithAttributes, TestIdProp } from '../../types';
import { useTestIds } from '../../hooks';
import { withTestIds } from '../../utils';
import VisuallyHiddenText from '../VisuallyHiddenText';

import { getKeyboardTestIds } from './Badges.test-ids';

const osxKeyMapper: { [key: string]: string | JSX.Element } = {
  Command: '⌘',
  Cmd: '⌘',
  Alt: '⌥',
  Option: '⌥',
  Control: '⌃',
  Return: (
    <>
      <span aria-hidden>↵</span>
      <VisuallyHiddenText>Return</VisuallyHiddenText>
    </>
  ),
  Delete: (
    <>
      <span aria-hidden>Del</span>
      <VisuallyHiddenText>Delete</VisuallyHiddenText>
    </>
  ),
  CommandOrControl: '⌘'
};

const windowsKeyMapper: { [key: string]: string | JSX.Element } = {
  Control: 'Ctrl',
  Alt: 'Alt',
  Option: 'Alt',
  Meta: (
    <>
      <span aria-hidden>⊞</span>
      <VisuallyHiddenText>Windows</VisuallyHiddenText>
    </>
  ),
  Command: (
    <>
      <span aria-hidden>⊞</span>
      <VisuallyHiddenText>Windows</VisuallyHiddenText>
    </>
  ),
  Backspace: 'Backspace',
  CommandOrControl: 'Ctrl'
};

const generalKeyMapper: { [key: string]: string | JSX.Element } = {
  'Page up': (
    <>
      <span aria-hidden>Pg up</span>
      <VisuallyHiddenText>Page up</VisuallyHiddenText>
    </>
  ),
  'Page down': (
    <>
      <span aria-hidden>Pg dn</span>
      <VisuallyHiddenText>Page down</VisuallyHiddenText>
    </>
  ),
  Ins: 'Insert',
  Shift: (
    <>
      <span aria-hidden>⇧</span>
      <VisuallyHiddenText>Shift</VisuallyHiddenText>
    </>
  ),
  Up: (
    <>
      <span aria-hidden>↑</span>
      <VisuallyHiddenText>Up</VisuallyHiddenText>
    </>
  ),
  Down: (
    <>
      <span aria-hidden>↓</span>
      <VisuallyHiddenText>Down</VisuallyHiddenText>
    </>
  ),
  Left: (
    <>
      <span aria-hidden>←</span>
      <VisuallyHiddenText>Left</VisuallyHiddenText>
    </>
  ),
  Right: (
    <>
      <span aria-hidden>→</span>
      <VisuallyHiddenText>Right</VisuallyHiddenText>
    </>
  ),
  Enter: (
    <>
      <span aria-hidden>↵</span>
      <VisuallyHiddenText>Enter</VisuallyHiddenText>
    </>
  ),
  Escape: (
    <>
      <span aria-hidden>Esc</span>
      <VisuallyHiddenText>Escape</VisuallyHiddenText>
    </>
  )
};

export const StyledKeyboard = styled.kbd(
  ({
    theme: {
      base,
      components: {
        text: { primary },
        badges: { keyboard }
      }
    }
  }) => {
    return css`
      background-color: ${keyboard['background-color']};
      color: ${base.palette['foreground-color']};
      display: inline-block;
      position: relative;
      font-family: ${base['font-family']};
      font-weight: ${primary['font-weight']};
      overflow: hidden;
      white-space: nowrap;
      padding: 0.125rem;
      border: 0.0125rem solid ${keyboard['border-color']};
      border-radius: calc(${base['border-radius']} / 4);
      text-transform: capitalize;
      text-align: center;
      min-width: 1.25rem;
      height: 1.25rem;
      text-overflow: ellipsis;
      line-height: 1rem;
      inset-block-start: calc(1.25rem / 4 - 0.125rem);
    `;
  }
);
StyledKeyboard.defaultProps = defaultThemeProp;

export type KeyboardProps = WithAttributes<
  'kbd',
  NoChildrenProp & TestIdProp & { keyName: string }
>;

export default withTestIds(
  forwardRef<RefElement<KeyboardProps>, PropsWithoutRef<KeyboardProps>>(function Keyboard(
    { testId, keyName, ...restProps },
    ref
  ) {
    const { windows: isWindows } = useOS();
    const testIds = useTestIds(testId, getKeyboardTestIds);

    return (
      <StyledKeyboard data-testid={testIds.root} {...restProps} ref={ref}>
        {(isWindows && windowsKeyMapper[keyName]) ||
          osxKeyMapper[keyName] ||
          generalKeyMapper[keyName] ||
          keyName}
      </StyledKeyboard>
    );
  }),
  getKeyboardTestIds
);
